import { type ReactNode, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AddressesContext, type AddressesContextProps } from '../contexts/addresses.context';
import { useAuth } from '@/modules/auth/hooks/use-auth';
import { useServices } from '@/shared/hooks/use-services';
import { useGeolocation } from '../hooks/use-geolocation';
import type { UserAddress } from '../types/user-address';
import { enhancedToast } from '@/shared/utils/enhanced-toast';

interface AddressesProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function AddressesProvider({ children, enabled = true }: AddressesProviderProps) {
  const { isAuthenticated } = useAuth();
  const { listUserAddresses, convertCoordinatesToAddress, getSelectedAddress, setSelectedAddress } =
    useServices();
  const { coordinates } = useGeolocation();
  const queryClient = useQueryClient();

  // Query para endereços salvos
  const addressesQuery = useQuery({
    queryKey: ['addresses'],
    queryFn: async () => await listUserAddresses.execute(),
    enabled: enabled && isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });

  // Query para endereço selecionado
  const selectedAddressQuery = useQuery({
    queryKey: ['selected-address'],
    queryFn: async () => await getSelectedAddress.execute(),
    enabled: enabled,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Query para endereço por geolocalização
  const geolocationAddressQuery = useQuery({
    queryKey: ['geolocation-address', coordinates?.latitude, coordinates?.longitude],
    queryFn: async () =>
      await convertCoordinatesToAddress.execute({
        latitude: coordinates?.latitude || null,
        longitude: coordinates?.longitude || null,
      }),
    enabled: enabled && !!coordinates?.latitude && !!coordinates?.longitude,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
    retry: false, // Sem retry para reverse geocoding
    meta: {
      persist: true,
    },
  });

  // Mutation para definir endereço selecionado
  const setSelectedAddressMutation = useMutation({
    mutationFn: async (address: UserAddress) => {
      await setSelectedAddress.execute(address);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selected-address'] });
    },
    onError: (error) => {
      console.error('Erro ao definir endereço selecionado:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      enhancedToast.error(`Erro ao definir endereço selecionado: ${errorMessage}`);
    },
  });

  const handleChangeSelectedAddress =
    async (address: UserAddress): Promise<void> => {
      await setSelectedAddressMutation.mutateAsync(address);
    };


  // Lógica para determinar o endereço selecionado
  const selectedAddress = useMemo(() => {
    const selectedAddressData = selectedAddressQuery.data;
    const addressesData = addressesQuery.data;

    // Se já há um endereço selecionado, retorna ele
    if (selectedAddressData && selectedAddressData.id) return selectedAddressData;

    // Se há endereços salvos, retorna o primeiro
    if (addressesData && addressesData.length > 0) return addressesData[0];

    // Se não há endereços salvos, retorna null
    return null;
  }, [selectedAddressQuery.data, addressesQuery.data]);

  // Memoiza o endereço para evitar re-renderizações desnecessárias
  const address = useMemo(
    () => ({
      geolocation: geolocationAddressQuery.data || null,
      selected: selectedAddress,
    }),
    [geolocationAddressQuery.data, selectedAddress]
  );

  const ctx: AddressesContextProps = {
    addresses: addressesQuery.data || [],
    address,
    isLoading:
      addressesQuery.isLoading ||
      selectedAddressQuery.isLoading ||
      geolocationAddressQuery.isLoading,
    handleChangeSelectedAddress,
  };

  return <AddressesContext.Provider value={ctx}>{children}</AddressesContext.Provider>;
}
